import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { Download, FileText, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { toast } from "sonner";
import { LOGO_DATA_URL } from "@/utils/logoData";
import DOMPurify from "dompurify";
import axios from "axios";

// Default UnCloned brand colors
const BRAND_PINK = "#df1283";
const BRAND_ORANGE = "#FF7901";
const BRAND_PURPLE = "#4A0A66";

// Logo URL - using the mobile logo from public folder (for print preview only)
const LOGO_URL = `${window.location.origin}/new-logo-homepage.png`;

// API URL for images - dynamic for custom domains
import { API } from '@/utils/api';
const API_URL = API.replace('/api', '');

// Track PDF export in backend for analytics
const trackPdfExport = async (entryId, type = "single", entryCount = 1) => {
  try {
    await axios.post(`${API}/track/pdf-export`, {
      entry_id: entryId,
      type: type,
      entry_count: entryCount
    }, { withCredentials: true });
  } catch (err) {
    // Silently fail - don't block PDF download for tracking
    console.warn("Failed to track PDF export:", err);
  }
};

// Create SVG date badge with customizable colors
const createDateBadgeSVG = (dateText, brandColor1 = BRAND_PINK, brandColor2 = BRAND_ORANGE, isB2B = false) => {
  const width = 120;
  const height = 28;
  // For B2B, use solid color fill; for main app, use gradient
  const fillValue = isB2B ? brandColor1 : "url(#badgeGrad)";
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
      <defs>
        <linearGradient id="badgeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:${brandColor1};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${brandColor2 || brandColor1};stop-opacity:1" />
        </linearGradient>
      </defs>
      <rect width="${width}" height="${height}" rx="14" fill="${fillValue}"/>
      <text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" fill="white" font-family="Helvetica, Arial, sans-serif" font-size="12" font-weight="600">${dateText}</text>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Helper functions - Updated mood map to match current moods
const MOOD_MAP = {
  uncloned: { emoji: "✨", label: "UnCloned" },  // Main app special mood
  custom_branded: { emoji: "✨", label: "Custom" },  // B2B custom mood fallback
  magical: { emoji: "✨", label: "Magical" },
  amazing: { emoji: "🌟", label: "Amazing" },
  loved: { emoji: "💖", label: "Loved" },
  on_fire: { emoji: "🔥", label: "On Fire" },
  hopeful: { emoji: "🌈", label: "Hopeful" },
  dreamy: { emoji: "💫", label: "Dreamy" },
  free: { emoji: "🦋", label: "Free" },
  peaceful: { emoji: "🌸", label: "Peaceful" },
  energized: { emoji: "⚡", label: "Energized" },
  calm: { emoji: "🌙", label: "Calm" },
  thoughtful: { emoji: "💭", label: "Thoughtful" },
  focused: { emoji: "🎯", label: "Focused" },
  tired: { emoji: "😴", label: "Tired" },
  melancholy: { emoji: "🌧️", label: "Melancholy" },
  strong: { emoji: "💪", label: "Strong" },
  // Legacy moods for backwards compatibility
  happy: { emoji: "😊", label: "Happy" },
  excited: { emoji: "🤩", label: "Excited" },
  grateful: { emoji: "🙏", label: "Grateful" },
  confident: { emoji: "💪", label: "Confident" },
  creative: { emoji: "🎨", label: "Creative" },
  anxious: { emoji: "😰", label: "Anxious" },
  sad: { emoji: "😢", label: "Sad" },
  angry: { emoji: "😤", label: "Angry" },
  confused: { emoji: "😕", label: "Confused" },
  stressed: { emoji: "😫", label: "Stressed" },
};

const getMoodEmoji = (mood, customMood = null) => {
  // Check if it's the custom mood (value is "uncloned" or "custom_branded" for custom moods)
  if (customMood?.enabled && (mood === "uncloned" || mood === "custom_branded")) {
    return "✨"; // Use sparkle for custom mood in PDF since we can't render custom images easily
  }
  return MOOD_MAP[mood]?.emoji || "✨";
};

const getMoodLabel = (mood, customMood = null) => {
  // Check if it's the custom mood (value is "uncloned" or "custom_branded" for custom moods)
  if (customMood?.enabled && (mood === "uncloned" || mood === "custom_branded")) {
    return customMood.label || "Special";
  }
  return MOOD_MAP[mood]?.label || "Feeling";
};

const formatTime = (dateString) => {
  try {
    const date = new Date(dateString);
    return format(date, "h:mm a");
  } catch {
    return "";
  }
};

const stripHtml = (html) => {
  const tmp = document.createElement("div");
  tmp.innerHTML = DOMPurify.sanitize(html || '');
  return tmp.textContent || tmp.innerText || "";
};

// Convert emoji to image data URL
const emojiToImage = (emoji, size = 64) => {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.font = `${size * 0.8}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(emoji, size / 2, size / 2);
    resolve(canvas.toDataURL("image/png"));
  });
};

// Convert SVG to PNG data URL
const svgToPng = (svgUrl, width = 400, height = 200) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      
      // Calculate scaling to fit while maintaining aspect ratio
      const scale = Math.min(width / img.width, height / img.height);
      const x = (width - img.width * scale) / 2;
      const y = (height - img.height * scale) / 2;
      
      ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      console.error("Failed to load SVG");
      reject(new Error("SVG load failed"));
    };
    img.src = svgUrl;
  });
};

// Print icon that actually prints the entry
// Supports B2B white-labeling with custom logo and brand colors
export const PrintEntryButton = ({ entry, clientBranding = null, customMood = null }) => {
  // Extract B2B branding if provided
  const logoUrl = clientBranding?.logo_url || null;
  const brandColor = clientBranding?.brand_color || BRAND_PINK;
  const accentColor = clientBranding?.accent_color || clientBranding?.brand_color || BRAND_ORANGE;
  const appName = clientBranding?.app_name || 'UnCloned® Journal';
  const isB2B = !!clientBranding?.brand_color; // B2B uses flat colors, main app uses gradients
  
  // Helper to get background style - flat for B2B, gradient for main app
  const getBgStyle = (color1, color2) => isB2B ? color1 : `linear-gradient(135deg, ${color1}, ${color2})`;
  const getVerticalBgStyle = (color1, color2) => isB2B ? color1 : `linear-gradient(180deg, ${color1}, ${color2})`;
  const getHorizontalBgStyle = (color1, color2) => isB2B ? color1 : `linear-gradient(90deg, ${color1}, ${color2}, ${color1})`;
  
  const handlePrint = async () => {
    // On mobile, use the download PDF approach instead of browser print
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Use download approach for mobile - more reliable
      await downloadEntryAsPdf(entry, clientBranding, customMood);
      return;
    }
    
    // Desktop: Use print preview popup with pre-paginated pages
    const width = 950;
    const height = 750;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    const printWindow = window.open('', '_blank', `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
    if (!printWindow) {
      toast.error('Please allow popups to print your entry');
      return;
    }

    // Sanitize all user-provided content to prevent XSS
    const sanitizedTitle = DOMPurify.sanitize(entry.title || 'Journal Entry');
    const sanitizedContent = DOMPurify.sanitize(entry.content || '');
    const sanitizedAiReflection = entry.ai_reflection ? DOMPurify.sanitize(entry.ai_reflection) : '';

    // Check if this is a custom mood with an image (B2B)
    const isCustomMoodWithImage = customMood?.enabled && 
      (entry.mood === "uncloned" || entry.mood === "custom_branded") && 
      customMood?.imageUrl;
    
    // Check if this is the main app's "uncloned" mood (no B2B custom mood)
    const isMainAppUnclonedMood = (entry.mood === "uncloned") && !customMood?.enabled;

    const moodEmoji = getMoodEmoji(entry.mood, customMood);
    const moodLabel = getMoodLabel(entry.mood, customMood);
    const timeWritten = entry.created_at ? formatTime(entry.created_at) : '';
    
    // Build mood display HTML for print:
    // 1. B2B custom mood with PDF image -> use server-hosted image (no CORS issues)
    // 2. B2B custom mood without PDF image -> use external image with fallback
    // 3. Main app "uncloned" mood -> use the UnCloned mood icon with gradient
    // 4. Otherwise -> use emoji
    let moodDisplayHtml;
    if (isCustomMoodWithImage && customMood?.pdfImageUrl) {
      // Server-hosted PDF image - will always work
      moodDisplayHtml = `<img src="${customMood.pdfImageUrl}" alt="${moodLabel}" style="width: 44px; height: 44px; object-fit: contain;" />`;
    } else if (isCustomMoodWithImage) {
      // External image - use fallback system
      const fallbackColor = brandColor || '#df1283';
      const labelInitial = (moodLabel || 'M').charAt(0).toUpperCase();
      moodDisplayHtml = `
        <div style="width: 44px; height: 44px; margin: 0 auto; position: relative; display: inline-block;">
          <div style="width: 44px; height: 44px; background: ${fallbackColor}; border-radius: 50%; display: flex; align-items: center; justify-content: center; position: absolute; top: 0; left: 0;">
            <span style="color: white; font-size: 20px; font-weight: bold;">${labelInitial}</span>
          </div>
          <img src="${customMood.imageUrl}" alt="${moodLabel}" 
            style="width: 44px; height: 44px; object-fit: contain; position: absolute; top: 0; left: 0; border-radius: 50%; background: white;"
            onerror="this.style.display='none'" />
        </div>`;
    } else if (isMainAppUnclonedMood) {
      moodDisplayHtml = `<div style="width: 44px; height: 44px; margin: 0 auto; background: linear-gradient(135deg, ${brandColor}, ${accentColor}, #D0FF0B, #0FFFE2); border-radius: 50%; display: flex; align-items: center; justify-content: center;">
        <img src="/mood-uncloned.png" alt="UnCloned" style="width: 32px; height: 32px; object-fit: contain; filter: brightness(0) invert(1);" />
      </div>`;
    } else {
      moodDisplayHtml = moodEmoji;
    }
    
    // Build images HTML - image IDs are system-generated, encode for safety
    let imagesHtml = '';
    if (entry.images && entry.images.length > 0) {
      imagesHtml = `
        <div class="images-section">
          <div class="images-title">Photos</div>
          <div class="images-grid">
            ${entry.images.map(imageId => `
              <img src="${API_URL}/api/images/${encodeURIComponent(imageId)}" class="entry-image" alt="Journal photo" />
            `).join('')}
          </div>
        </div>
      `;
    }
    
    // AI Reflection HTML - use sanitized content
    let aiReflectionHtml = '';
    if (sanitizedAiReflection) {
      aiReflectionHtml = `
        <div class="ai-reflection">
          <div class="ai-reflection-header">
            <span class="ai-icon">🧠</span>
            <span class="ai-reflection-title">AI Reflection</span>
            ${entry.ai_reflection_date ? `<span class="ai-reflection-date">${new Date(entry.ai_reflection_date).toLocaleDateString()}</span>` : ''}
          </div>
          <div class="ai-reflection-content">
            ${sanitizedAiReflection.split('\n\n').map(p => `<p>${DOMPurify.sanitize(p)}</p>`).join('')}
          </div>
        </div>
      `;
    }

    // Determine logo to use - B2B client logo or default UnCloned logo
    const pdfLogoUrl = logoUrl || LOGO_URL;
    const pdfAppName = appName;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${sanitizedTitle} - ${pdfAppName}</title>
        <style>
          @page {
            size: 8.5in 11in;
            margin: 0;
          }
          
          * { margin: 0; padding: 0; box-sizing: border-box; }
          
          body { 
            font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; 
            background: #f0f0f0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .print-toolbar {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            background: ${getBgStyle(brandColor, accentColor)};
            padding: 12px 25px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            z-index: 1000;
          }
          .print-toolbar h1 { color: white; font-size: 15px; font-weight: 600; }
          .toolbar-buttons { display: flex; gap: 10px; }
          .print-btn {
            background: ${getBgStyle(brandColor, accentColor)};
            color: white;
            border: none;
            padding: 8px 22px;
            border-radius: 20px;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
          }
          .close-btn {
            background: rgba(255,255,255,0.2);
            color: white;
            border: 1px solid rgba(255,255,255,0.3);
            padding: 8px 18px;
            border-radius: 20px;
            font-size: 13px;
            cursor: pointer;
          }
          
          @media print {
            .print-toolbar { display: none !important; }
            body { background: white !important; }
            .pages-container { margin: 0 !important; padding: 0 !important; gap: 0 !important; }
            .page { 
              box-shadow: none !important;
              margin: 0 !important;
              page-break-after: always;
              break-after: page;
            }
            .page:last-child { page-break-after: auto; break-after: auto; }
          }
          
          .pages-container {
            margin-top: 60px;
            padding: 20px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 25px;
          }
          
          .page {
            width: 8.5in;
            height: 11in;
            background: white;
            box-shadow: 0 3px 15px rgba(0,0,0,0.12);
            position: relative;
            overflow: hidden;
            flex-shrink: 0;
          }
          
          .page-header {
            background: ${getVerticalBgStyle(brandColor, accentColor)};
            padding: 16px 35px;
            text-align: center;
          }
          .page-header img { max-width: 260px; height: auto; }
          .accent-line {
            height: 4px;
            background: ${getHorizontalBgStyle(brandColor, accentColor)};
          }
          
          .page-content {
            padding: 20px 40px;
            height: calc(11in - 85px - 45px - 20px); /* page - header - footer - padding */
            overflow: hidden;
          }
          
          .page-footer {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 12px 35px;
            background: #f8f8f8;
            border-top: 1px solid #e5e5e5;
            text-align: center;
            font-size: 10px;
            color: #888;
          }
          
          .entry-header { margin-bottom: 15px; }
          .emoji { font-size: 44px; text-align: center; margin-bottom: 10px; }
          .title { font-size: 22px; font-weight: 700; text-align: center; margin-bottom: 10px; color: #222; }
          .meta { 
            display: flex; 
            justify-content: center; 
            align-items: center; 
            gap: 15px; 
            flex-wrap: wrap;
            margin-bottom: 15px;
          }
          .date-badge {
            background: ${getBgStyle(brandColor, accentColor)};
            color: white;
            padding: 6px 18px;
            border-radius: 18px;
            font-size: 11px;
            font-weight: 600;
          }
          .mood { color: ${brandColor}; font-size: 11px; font-weight: 600; }
          .time { color: #777; font-size: 10px; }
          .divider { height: 1px; background: #eee; margin-bottom: 15px; }
          
          .entry-content { font-size: 13px; line-height: 1.7; color: #333; }
          .entry-content p { margin-bottom: 0.8em; }
          
          .images-section { margin-top: 15px; }
          .images-title { font-size: 13px; font-weight: 600; color: ${brandColor}; margin-bottom: 10px; }
          .images-grid { text-align: center; }
          .entry-image {
            max-width: 100%;
            max-height: 220px;
            width: auto;
            height: auto;
            object-fit: cover;
            border-radius: 8px;
            border: 2px solid #f0f0f0;
            margin: 5px;
          }
          
          .ai-reflection {
            margin-top: 15px;
            padding: 14px;
            background: linear-gradient(135deg, #f8f0ff, #fff0f5);
            border-radius: 8px;
            border-left: 3px solid ${brandColor};
          }
          .ai-reflection-header { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
          .ai-icon { font-size: 14px; }
          .ai-reflection-title { font-size: 11px; font-weight: 700; color: ${brandColor}; }
          .ai-reflection-date { font-size: 9px; color: #888; margin-left: auto; }
          .ai-reflection-content { font-size: 10px; line-height: 1.6; color: #555; font-style: italic; }
          .ai-reflection-content p { margin-bottom: 6px; }
        </style>
      </head>
      <body>
        <div class="print-toolbar">
          <h1>Print Preview - ${entry.title || 'Journal Entry'}</h1>
          <div class="toolbar-buttons">
            <button class="print-btn" onclick="window.print()">Print</button>
            <button class="close-btn" onclick="window.close()">Close</button>
          </div>
        </div>
        
        <!-- Hidden measurement container -->
        <div id="measure-box" style="position:absolute;left:-9999px;width:calc(8.5in - 80px);padding:20px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
          <div id="header-section" class="entry-header">
            <div class="emoji">${moodDisplayHtml}</div>
            ${entry.title ? `<h1 class="title">${entry.title}</h1>` : ''}
            <div class="meta">
              <span class="date-badge">${entry.date}</span>
              <span class="mood">Mood: ${moodLabel}</span>
              ${timeWritten ? `<span class="time">Written at ${timeWritten}</span>` : ''}
            </div>
            <div class="divider"></div>
          </div>
          <div id="content-section" class="entry-content">${DOMPurify.sanitize(entry.content || '')}</div>
          <div id="images-section">${imagesHtml}</div>
          <div id="ai-section">${DOMPurify.sanitize(aiReflectionHtml)}</div>
        </div>
        
        <div class="pages-container" id="pages-container"></div>
        
        <script>
          function createPage(pageNum, totalPages) {
            const page = document.createElement('div');
            page.className = 'page';
            page.innerHTML = \`
              <div class="page-header">
                <img src="${pdfLogoUrl}" alt="${pdfAppName}" />
              </div>
              <div class="accent-line"></div>
              <div class="page-content" data-page="\${pageNum}"></div>
              <div class="page-footer">
                \${totalPages > 1 ? 'Page ' + pageNum + ' of ' + totalPages + ' | ' : ''}Created with love using <strong>${pdfAppName}</strong>
              </div>
            \`;
            return page;
          }
          
          function paginate() {
            const container = document.getElementById('pages-container');
            const measureBox = document.getElementById('measure-box');
            
            // Get content sections
            const headerSection = document.getElementById('header-section');
            const contentSection = document.getElementById('content-section');
            const imagesSection = document.getElementById('images-section');
            const aiSection = document.getElementById('ai-section');
            
            // Calculate available height per page (in pixels)
            // 11in page - 85px header - 45px footer - 40px padding = ~876px usable
            const PAGE_CONTENT_HEIGHT = 876;
            
            // Collect all content blocks with their heights
            const blocks = [];
            
            // Header (emoji, title, date, divider)
            blocks.push({ el: headerSection.cloneNode(true), height: headerSection.offsetHeight });
            
            // Content paragraphs - split into individual elements
            const contentEl = contentSection.cloneNode(true);
            if (contentEl.children.length > 0) {
              Array.from(contentEl.children).forEach(child => {
                const wrapper = document.createElement('div');
                wrapper.appendChild(child.cloneNode(true));
                measureBox.appendChild(wrapper);
                blocks.push({ el: child.cloneNode(true), height: wrapper.offsetHeight + 10 });
                measureBox.removeChild(wrapper);
              });
            } else if (contentEl.innerHTML.trim()) {
              blocks.push({ el: contentEl, height: contentSection.offsetHeight });
            }
            
            // Images section
            if (imagesSection.innerHTML.trim()) {
              blocks.push({ el: imagesSection.cloneNode(true), height: imagesSection.offsetHeight + 20 });
            }
            
            // AI section
            if (aiSection.innerHTML.trim()) {
              blocks.push({ el: aiSection.cloneNode(true), height: aiSection.offsetHeight + 20 });
            }
            
            // Distribute blocks across pages
            const pages = [];
            let currentPage = [];
            let currentHeight = 0;
            
            blocks.forEach(block => {
              if (currentHeight + block.height > PAGE_CONTENT_HEIGHT && currentPage.length > 0) {
                pages.push(currentPage);
                currentPage = [];
                currentHeight = 0;
              }
              currentPage.push(block.el);
              currentHeight += block.height;
            });
            
            if (currentPage.length > 0) {
              pages.push(currentPage);
            }
            
            // Ensure at least one page
            if (pages.length === 0) {
              pages.push([headerSection.cloneNode(true)]);
            }
            
            const totalPages = pages.length;
            
            // Create page elements
            pages.forEach((pageBlocks, idx) => {
              const page = createPage(idx + 1, totalPages);
              const contentArea = page.querySelector('.page-content');
              pageBlocks.forEach(block => contentArea.appendChild(block));
              container.appendChild(page);
            });
            
            // Remove measurement container
            measureBox.remove();
          }
          
          // Wait for images then paginate
          window.onload = function() {
            const images = document.querySelectorAll('img');
            let loaded = 0;
            const total = images.length;
            
            if (total === 0) {
              setTimeout(paginate, 100);
              return;
            }
            
            images.forEach(img => {
              if (img.complete) {
                loaded++;
                if (loaded === total) setTimeout(paginate, 100);
              } else {
                img.onload = img.onerror = function() {
                  loaded++;
                  if (loaded === total) setTimeout(paginate, 100);
                };
              }
            });
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div 
      className="relative inline-flex items-center justify-center w-10 h-10 group cursor-pointer" 
      onClick={handlePrint}
      data-testid="print-entry-btn"
    >
      <span className="absolute -top-1 -right-1 text-[8px] animate-pulse">✨</span>
      <span className="absolute -bottom-1 -left-1 text-[8px] animate-pulse" style={{ animationDelay: '0.5s' }}>💖</span>
      
      <div 
        className="relative p-2 rounded-lg transition-all duration-300 hover:scale-110 hover:bg-[var(--theme-background-secondary)]"
        style={{
          background: `rgba(var(--theme-primary-rgb), 0.1)`,
        }}
      >
        <Printer className="w-5 h-5 text-[var(--theme-primary)]" />
      </div>
      
      <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
        <span className="text-xs px-2 py-1 rounded" style={{ color: 'var(--theme-text-secondary)', backgroundColor: 'var(--theme-background-secondary)' }}>Print</span>
      </div>
    </div>
  );
};

export const CutePrintIcon = PrintEntryButton;

// Helper to check if a color is light/white (returns true if very light)
const isLightColor = (hexColor) => {
  if (!hexColor) return false;
  // Remove # if present
  const hex = hexColor.replace('#', '');
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  // Calculate luminance (perceived brightness)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  // Consider it "light" if luminance > 0.85 (very light/white)
  return luminance > 0.85;
};

// Helper to get the best non-white color from client's palette for PDF display
const getPdfSafeColor = (clientBranding) => {
  if (!clientBranding) return BRAND_PINK;
  
  const brandColor = clientBranding.brand_color;
  const accentColor = clientBranding.accent_color;
  const secondaryColor = clientBranding.theme?.secondary_color;
  const textColor = clientBranding.theme?.text_color;
  
  // Try brand color first, if it's light, try alternatives
  if (brandColor && !isLightColor(brandColor)) {
    return brandColor;
  }
  // Try accent color
  if (accentColor && !isLightColor(accentColor)) {
    return accentColor;
  }
  // Try secondary color from theme
  if (secondaryColor && !isLightColor(secondaryColor)) {
    return secondaryColor;
  }
  // Try text color (usually dark)
  if (textColor && !isLightColor(textColor)) {
    return textColor;
  }
  // Fallback to a safe dark color
  return '#333333';
};

// Generate PDF with proper multi-page support for long entries
// Supports B2B white-labeling with custom logo and brand colors
export const downloadEntryAsPdf = async (entry, clientBranding = null, customMood = null) => {
  toast.info("Generating PDF...");
  
  // Extract B2B branding if provided
  const hasClientLogo = clientBranding?.logo_url && clientBranding.logo_url.trim() !== '';
  const logoDataUrl = hasClientLogo ? clientBranding.logo_url : (clientBranding ? null : LOGO_DATA_URL);
  const brandColor = clientBranding?.brand_color || BRAND_PINK;
  const accentColor = clientBranding?.accent_color || clientBranding?.brand_color || BRAND_ORANGE;
  const appName = clientBranding?.app_name || 'UnCloned® Journal';
  const isB2B = !!clientBranding?.brand_color; // B2B uses flat colors, main app uses gradients
  
  // Helper to get background style - flat for B2B, gradient for main app
  const getBgStyle = (color1, color2) => isB2B ? color1 : `linear-gradient(135deg, ${color1}, ${color2})`;
  const getVerticalBgStyle = (color1, color2) => isB2B ? color1 : `linear-gradient(180deg, ${color1}, ${color2})`;
  const getHorizontalBgStyle = (color1, color2) => isB2B ? color1 : `linear-gradient(90deg, ${color1}, ${color2}, ${color1})`;
  
  // Get a PDF-safe color (non-white) from the client's palette for logo background
  const pdfSafeColor = getPdfSafeColor(clientBranding);
  
  // Check if this is a custom mood with an image (B2B)
  const isCustomMoodWithImage = customMood?.enabled && 
    (entry.mood === "uncloned" || entry.mood === "custom_branded") && 
    customMood?.imageUrl;
  
  // Check if this is the main app's "uncloned" mood (no B2B custom mood)
  const isMainAppUnclonedMood = (entry.mood === "uncloned") && !customMood?.enabled;
  
  const moodEmoji = getMoodEmoji(entry.mood, customMood);
  const moodLabel = getMoodLabel(entry.mood, customMood);
  const timeWritten = entry.created_at ? formatTime(entry.created_at) : '';
  const dateBadgeSVG = createDateBadgeSVG(entry.date, brandColor, accentColor, isB2B);
  
  // Determine which image URL to use for PDFs:
  // 1. If pdfImageUrl exists (server-hosted), use it - guaranteed to work
  // 2. Otherwise fall back to imageUrl (may fail CORS)
  const pdfMoodImageUrl = customMood?.pdfImageUrl || customMood?.imageUrl;
  
  // Build mood display HTML:
  // 1. B2B custom mood with PDF image -> use server-hosted image (no CORS issues)
  // 2. B2B custom mood without PDF image -> use external image with fallback
  // 3. Main app "uncloned" mood -> use the UnCloned mood icon
  // 4. Otherwise -> use emoji
  let moodDisplayHtml;
  if (isCustomMoodWithImage && customMood?.pdfImageUrl) {
    // Server-hosted PDF image - will always work
    moodDisplayHtml = `<img src="${customMood.pdfImageUrl}" alt="${moodLabel}" style="width: 56px; height: 56px; object-fit: contain; margin: 0 auto; display: block;" />`;
  } else if (isCustomMoodWithImage) {
    // External image - use fallback system
    const fallbackColor = pdfSafeColor || brandColor || '#df1283';
    const labelInitial = (moodLabel || 'M').charAt(0).toUpperCase();
    moodDisplayHtml = `
      <div style="width: 56px; height: 56px; margin: 0 auto; position: relative;">
        <!-- Fallback: colored circle with initial (shows if image fails) -->
        <div style="width: 56px; height: 56px; background: ${fallbackColor}; border-radius: 50%; display: flex; align-items: center; justify-content: center; position: absolute; top: 0; left: 0;">
          <span style="color: white; font-size: 24px; font-weight: bold;">${labelInitial}</span>
        </div>
        <!-- Custom mood image (overlays fallback if loads successfully) -->
        <img src="${customMood.imageUrl}" alt="${moodLabel}" crossorigin="anonymous" 
          style="width: 56px; height: 56px; object-fit: contain; position: absolute; top: 0; left: 0; border-radius: 50%; background: white;"
          onerror="this.style.display='none'" />
      </div>`;
  } else if (isMainAppUnclonedMood) {
    // Use the UnCloned mood icon with gradient background for main app
    moodDisplayHtml = `<div style="width: 56px; height: 56px; margin: 0 auto; background: linear-gradient(135deg, ${BRAND_PINK}, ${BRAND_ORANGE}, #D0FF0B, #0FFFE2); border-radius: 50%; display: flex; align-items: center; justify-content: center;">
      <img src="/mood-uncloned.png" alt="UnCloned" style="width: 40px; height: 40px; object-fit: contain; filter: brightness(0) invert(1);" />
    </div>`;
  } else {
    moodDisplayHtml = `<span style="font-size: 56px; line-height: 1;">${moodEmoji}</span>`;
  }

  // Process content - sanitize to prevent XSS
  let processedContent = DOMPurify.sanitize(entry.content || '<p style="color: #999; font-style: italic;">No content</p>');
  processedContent = processedContent.replace(/<img /g, '<img crossorigin="anonymous" ');
  
  // Build images HTML
  let imagesHtml = '';
  if (entry.images && entry.images.length > 0) {
    imagesHtml = '<div style="margin-top: 20px; text-align: center;">';
    for (const imageId of entry.images) {
      imagesHtml += `<img src="${API_URL}/api/images/${imageId}" crossorigin="anonymous" style="max-width: 100%; max-height: 300px; width: auto; height: auto; border-radius: 12px; margin: 10px auto; display: inline-block; object-fit: cover; border: 2px solid #f0f0f0;" />`;
    }
    imagesHtml += '</div>';
  }

  // Logo section - show text if B2B client has no logo uploaded
  const logoSection = logoDataUrl 
    ? (hasClientLogo 
        // B2B client logo - display with CORS support
        ? `<img src="${logoDataUrl}" alt="${appName}" crossorigin="anonymous" style="width: 300px; height: auto; display: block; margin: 0 auto;" />`
        // Main app logo (LOGO_DATA_URL) - embedded base64, always works
        : `<img src="${logoDataUrl}" alt="${appName}" style="width: 350px; height: auto; display: block; margin: 0 auto;" />`)
    : `<h1 style="font-size: 32px; font-weight: 700; color: white; text-align: center; margin: 0; padding: 10px 0; text-shadow: 1px 1px 2px rgba(0,0,0,0.2);">${appName}</h1>`;

  // A4 dimensions in pixels at scale 2 (for sharp rendering)
  const PAGE_WIDTH = 794;
  const PAGE_HEIGHT = 1123;
  const SCALE = 2;
  const HEADER_HEIGHT = 110; // Header with logo + gradient line  
  const FOOTER_HEIGHT = 50;  // Footer area
  const MARGIN = 50;
  
  // Calculate usable content height per page
  const CONTENT_HEIGHT_FIRST_PAGE = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
  const CONTENT_HEIGHT_OTHER_PAGES = PAGE_HEIGHT - FOOTER_HEIGHT - 30; // 30px top margin on continuation pages

  // Step 1: Render header separately
  const headerContainer = document.createElement("div");
  headerContainer.style.cssText = `position: fixed; left: -9999px; top: 0; width: ${PAGE_WIDTH}px; background: white; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;`;
  headerContainer.innerHTML = `
    <div style="background: ${getVerticalBgStyle(brandColor, accentColor)}; padding: 25px 50px; text-align: center;">
      ${logoSection}
    </div>
    <div style="height: 4px; background: ${getHorizontalBgStyle(brandColor, accentColor)};"></div>
  `;
  document.body.appendChild(headerContainer);
  
  // Step 2: Render footer separately
  const footerContainer = document.createElement("div");
  footerContainer.style.cssText = `position: fixed; left: -9999px; top: 0; width: ${PAGE_WIDTH}px; background: white;`;
  footerContainer.innerHTML = `
    <div style="width: ${PAGE_WIDTH}px; padding: 15px ${MARGIN}px; background: #f8f8f8; border-top: 1px solid #e5e5e5; text-align: center; font-size: 11px; color: #888;">
      Created with ❤️ using <strong>${appName}</strong>
    </div>
  `;
  document.body.appendChild(footerContainer);
  
  // Step 3: Render JUST the content (no header/footer) to get full height
  const contentContainer = document.createElement("div");
  contentContainer.style.cssText = `position: fixed; left: -9999px; top: 0; width: ${PAGE_WIDTH}px; background: white; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;`;
  contentContainer.innerHTML = `
    <div style="padding: 20px ${MARGIN}px 40px ${MARGIN}px;">
      <div style="text-align: center; margin: 0 0 12px 0;">${moodDisplayHtml}</div>
      ${entry.title ? `<h1 style="font-size: 26px; font-weight: 700; text-align: center; margin: 0 0 16px 0; color: #222;">${entry.title}</h1>` : ''}
      <div style="text-align: center; margin-bottom: 20px; display: flex; align-items: center; justify-content: center; gap: 20px; flex-wrap: wrap;">
        <img src="${dateBadgeSVG}" alt="${entry.date}" style="height: 28px; width: 120px;" />
        <span style="color: ${brandColor}; font-size: 13px; font-weight: 600;">Mood: ${moodLabel}</span>
        ${timeWritten ? `<span style="color: #777; font-size: 12px;">Written at ${timeWritten}</span>` : ''}
      </div>
      <div style="height: 1px; background: #eee; margin-bottom: 20px;"></div>
      <div style="font-size: 15px; line-height: 1.8; color: #333;">
        ${processedContent}
      </div>
      ${imagesHtml}
      ${entry.ai_reflection ? `
      <div style="margin-top: 30px; padding: 20px; background: linear-gradient(135deg, #f8f0ff, #fff0f5); border-radius: 12px; border-left: 4px solid ${brandColor};">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
          <span style="font-size: 18px;">🧠</span>
          <span style="font-size: 14px; font-weight: 700; color: ${brandColor};">AI Reflection</span>
          ${entry.ai_reflection_date ? `<span style="font-size: 10px; color: #888; margin-left: auto;">${new Date(entry.ai_reflection_date).toLocaleDateString()}</span>` : ''}
        </div>
        <div style="font-size: 12px; line-height: 1.7; color: #555; font-style: italic;">
          ${entry.ai_reflection.split('\n\n').map(p => `<p style="margin-bottom: 10px;">${p}</p>`).join('')}
        </div>
      </div>
      ` : ''}
    </div>
  `;
  document.body.appendChild(contentContainer);
  
  // Wait for all images to load
  const allContainers = [headerContainer, footerContainer, contentContainer];
  for (const container of allContainers) {
    const imgs = container.querySelectorAll('img');
    await Promise.all(Array.from(imgs).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise(resolve => { img.onload = resolve; img.onerror = resolve; });
    }));
  }
  await new Promise(resolve => setTimeout(resolve, 300));

  try {
    // Render header, footer, and content as separate canvases
    const headerCanvas = await html2canvas(headerContainer, {
      scale: SCALE, useCORS: true, allowTaint: false, backgroundColor: "#4A0A66", logging: false, width: PAGE_WIDTH,
    });
    
    const footerCanvas = await html2canvas(footerContainer, {
      scale: SCALE, useCORS: true, allowTaint: false, backgroundColor: "#f8f8f8", logging: false, width: PAGE_WIDTH,
    });
    
    const contentCanvas = await html2canvas(contentContainer, {
      scale: SCALE, useCORS: true, allowTaint: false, backgroundColor: "#ffffff", logging: false, width: PAGE_WIDTH,
    });
    
    // Cleanup
    document.body.removeChild(headerContainer);
    document.body.removeChild(footerContainer);
    document.body.removeChild(contentContainer);

    // Calculate pages needed - EVERY page has header + footer + content padding
    const scaledContentHeight = contentCanvas.height;
    const CONTENT_TOP_PADDING = 40 * SCALE;  // Increased space between header and content
    const CONTENT_BOTTOM_PADDING = 40 * SCALE; // Increased space between content and footer
    const contentHeightPerPage = (PAGE_HEIGHT * SCALE) - headerCanvas.height - footerCanvas.height - CONTENT_TOP_PADDING - CONTENT_BOTTOM_PADDING;
    
    // Helper: Find a safe break point by scanning for white/light horizontal lines
    // This prevents cutting text mid-line by finding paragraph gaps
    // Also detects colored boxes (like AI reflection) and breaks BEFORE them
    const findSafeBreakPoint = (canvas, targetY, searchRange = 200 * SCALE) => {
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const contentAreaWidth = width - (2 * MARGIN * SCALE);
      
      // Helper to check if a row is "white/light" (standard content background)
      const isRowWhite = (y) => {
        if (y < 0 || y >= canvas.height) return false;
        const imageData = ctx.getImageData(MARGIN * SCALE, y, contentAreaWidth, 1);
        const pixels = imageData.data;
        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
          if (r < 245 || g < 245 || b < 245) {
            return false;
          }
        }
        return true;
      };
      
      // Helper to check if a row is inside a colored box (like AI reflection purple background)
      const isRowColored = (y) => {
        if (y < 0 || y >= canvas.height) return false;
        const imageData = ctx.getImageData(MARGIN * SCALE, y, contentAreaWidth, 1);
        const pixels = imageData.data;
        let coloredPixels = 0;
        const totalPixels = pixels.length / 4;
        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
          // Detect non-white backgrounds (purple/pink AI box is around RGB 248, 240, 255)
          // Also detect other colored backgrounds
          if (r < 250 || g < 250 || b < 250) {
            coloredPixels++;
          }
        }
        // If more than 80% of pixels are colored, it's a colored row
        return (coloredPixels / totalPixels) > 0.8;
      };
      
      // First, check if targetY is inside a colored box
      if (isRowColored(targetY)) {
        // We're inside a colored box - search upward to find where the box STARTS
        // and break BEFORE the box begins
        for (let y = targetY; y > targetY - searchRange * 2 && y > 0; y--) {
          // Check if we've exited the colored area (found white space above the box)
          if (isRowWhite(y)) {
            // Found white space - this is a safe break point (before the colored box)
            return y;
          }
        }
      }
      
      // Standard search: look for white rows (paragraph breaks)
      for (let y = targetY; y > targetY - searchRange && y > 0; y--) {
        if (isRowWhite(y)) {
          return y;
        }
      }
      
      // If no safe break found, return original target
      return targetY;
    };

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    let contentOffset = 0;
    let pageNum = 0;
    
    while (contentOffset < scaledContentHeight) {
      if (pageNum > 0) pdf.addPage();
      
      // Create page canvas
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = PAGE_WIDTH * SCALE;
      pageCanvas.height = PAGE_HEIGHT * SCALE;
      const ctx = pageCanvas.getContext('2d');
      
      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      
      // Draw header at top of EVERY page
      ctx.drawImage(headerCanvas, 0, 0);
      
      // Content starts BELOW header with padding
      const contentStartY = headerCanvas.height + CONTENT_TOP_PADDING;
      
      // Calculate ideal content height for this page
      const remainingContent = scaledContentHeight - contentOffset;
      let contentToDraw = Math.min(contentHeightPerPage, remainingContent);
      
      // If there's more content after this page, find a safe break point
      if (remainingContent > contentHeightPerPage) {
        const idealBreakY = contentOffset + contentHeightPerPage;
        const safeBreakY = findSafeBreakPoint(contentCanvas, idealBreakY);
        contentToDraw = safeBreakY - contentOffset;
        
        // Ensure we draw at least some content to avoid infinite loop
        if (contentToDraw < 200 * SCALE) {
          contentToDraw = Math.min(contentHeightPerPage, remainingContent);
        }
      }
      
      // Draw content slice (with proper spacing from header)
      if (contentToDraw > 0) {
        ctx.drawImage(
          contentCanvas,
          0, contentOffset,                    // Source position
          contentCanvas.width, contentToDraw,  // Source size
          0, contentStartY,                    // Destination position (below header + padding)
          contentCanvas.width, contentToDraw   // Destination size
        );
      }
      
      // Draw footer at bottom of EVERY page
      ctx.drawImage(footerCanvas, 0, (PAGE_HEIGHT * SCALE) - footerCanvas.height);
      
      // Add to PDF
      const imgData = pageCanvas.toDataURL("image/jpeg", 0.92);
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
      
      contentOffset += contentToDraw;
      pageNum++;
      
      // Safety: prevent infinite loop
      if (pageNum > 50) break;
    }

    const filename = entry.title 
      ? `${entry.title.replace(/[^a-z0-9]/gi, '_').substring(0, 30)}_${entry.date}.pdf`
      : `journal_entry_${entry.date}.pdf`;
    
    pdf.save(filename);
    
    // Track PDF export for analytics
    trackPdfExport(entry.id, "single", 1);
    
    toast.success("PDF downloaded! ✨");
  } catch (error) {
    console.error("PDF generation error:", error);
    toast.error("Failed to generate PDF. Please try again.");
  }
};

// Single entry PDF download button
// Supports B2B white-labeling with custom logo and brand colors
export const DownloadEntryButton = ({ entry, variant = "icon", clientBranding = null, customMood = null }) => {
  const handleDownload = () => downloadEntryAsPdf(entry, clientBranding, customMood);

  if (variant === "icon") {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); handleDownload(); }}
        className="p-2 rounded-full hover:bg-[var(--theme-background-secondary)] transition-colors"
        title="Download as PDF"
        data-testid="download-entry-pdf-btn"
      >
        <Download className="w-5 h-5 text-[var(--theme-primary)]" />
      </button>
    );
  }

  return (
    <Button
      onClick={handleDownload}
      className="flex items-center gap-2 hover:opacity-90"
      style={{ background: 'var(--theme-button-bg)' }}
      data-testid="download-entry-pdf-btn"
    >
      <Download className="w-4 h-4" />
      Download PDF
    </Button>
  );
};

// Download all entries button - with multi-page support per entry
// Supports B2B white-labeling with custom logo and brand colors
export const DownloadAllEntriesButton = ({ entries, clientBranding = null, customMood = null }) => {
  // Extract B2B branding if provided
  const hasClientLogo = clientBranding?.logo_url && clientBranding.logo_url.trim() !== '';
  const logoDataUrl = hasClientLogo ? clientBranding.logo_url : (clientBranding ? null : LOGO_DATA_URL);
  const brandColor = clientBranding?.brand_color || BRAND_PINK;
  const accentColor = clientBranding?.accent_color || clientBranding?.brand_color || BRAND_ORANGE;
  const appName = clientBranding?.app_name || 'UnCloned® Journal';
  const isB2B = !!clientBranding?.brand_color; // B2B uses flat colors, main app uses gradients
  
  // Helper to get background style - flat for B2B, gradient for main app
  const getBgStyle = (color1, color2) => isB2B ? color1 : `linear-gradient(135deg, ${color1}, ${color2})`;
  const getVerticalBgStyle = (color1, color2) => isB2B ? color1 : `linear-gradient(180deg, ${color1}, ${color2})`;
  const getHorizontalBgStyle = (color1, color2) => isB2B ? color1 : `linear-gradient(90deg, ${color1}, ${color2}, ${color1})`;
  
  // Get a PDF-safe color (non-white) from the client's palette for logo background
  const pdfSafeColor = getPdfSafeColor(clientBranding);
  
  const handleDownloadAll = async () => {
    if (!entries || entries.length === 0) {
      toast.error("No entries to download!");
      return;
    }

    toast.info(`Generating PDF with ${entries.length} entries... This may take a moment.`);

    try {
      // Logo section - show text if B2B client has no logo uploaded
      const logoSection = logoDataUrl 
        ? (hasClientLogo 
            // B2B client logo - display with CORS support
            ? `<img src="${logoDataUrl}" alt="${appName}" crossorigin="anonymous" style="width: 300px; height: auto; display: block; margin: 0 auto;" />`
            // Main app logo - embedded base64, always works
            : `<img src="${logoDataUrl}" alt="${appName}" style="width: 350px; height: auto; display: block; margin: 0 auto;" />`)
        : `<h1 style="font-size: 32px; font-weight: 700; color: white; text-align: center; margin: 0; padding: 10px 0; text-shadow: 1px 1px 2px rgba(0,0,0,0.2);">${appName}</h1>`;

      // Sort entries chronologically (oldest first)
      const sortedEntries = [...entries].sort((a, b) => {
        const dateA = new Date(a.created_at || a.date);
        const dateB = new Date(b.created_at || b.date);
        return dateA - dateB;
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Page dimensions
      const PAGE_WIDTH = 794;
      const PAGE_HEIGHT = 1123;
      const SCALE = 2;
      const HEADER_HEIGHT = 110;
      const FOOTER_HEIGHT = 50;
      const MARGIN = 50;

      let isFirstPdfPage = true;

      for (let entryIndex = 0; entryIndex < sortedEntries.length; entryIndex++) {
        const entry = sortedEntries[entryIndex];
        
        // Check if this is a custom mood with an image (B2B)
        const isCustomMoodWithImage = customMood?.enabled && 
          (entry.mood === "uncloned" || entry.mood === "custom_branded") && 
          customMood?.imageUrl;
        
        // Check if this is the main app's "uncloned" mood (no B2B custom mood)
        const isMainAppUnclonedMood = (entry.mood === "uncloned") && !customMood?.enabled;
        
        const moodEmoji = getMoodEmoji(entry.mood, customMood);
        const moodLabel = getMoodLabel(entry.mood, customMood);
        const timeWritten = entry.created_at ? formatTime(entry.created_at) : '';
        const dateBadgeSVG = createDateBadgeSVG(entry.date, brandColor, accentColor, isB2B);
        
        // Build mood display HTML:
        // 1. B2B custom mood with PDF image -> use server-hosted image (no CORS issues)
        // 2. B2B custom mood without PDF image -> use external image with fallback
        // 3. Main app "uncloned" mood -> use the UnCloned mood icon
        // 4. Otherwise -> use emoji
        let moodDisplayHtml;
        if (isCustomMoodWithImage && customMood?.pdfImageUrl) {
          // Server-hosted PDF image - will always work
          moodDisplayHtml = `<img src="${customMood.pdfImageUrl}" alt="${moodLabel}" style="width: 56px; height: 56px; object-fit: contain; margin: 0 auto; display: block;" />`;
        } else if (isCustomMoodWithImage) {
          // External image - use fallback system
          const fallbackColor = pdfSafeColor || brandColor || '#df1283';
          const labelInitial = (moodLabel || 'M').charAt(0).toUpperCase();
          moodDisplayHtml = `
            <div style="width: 56px; height: 56px; margin: 0 auto; position: relative;">
              <!-- Fallback: colored circle with initial (shows if image fails) -->
              <div style="width: 56px; height: 56px; background: ${fallbackColor}; border-radius: 50%; display: flex; align-items: center; justify-content: center; position: absolute; top: 0; left: 0;">
                <span style="color: white; font-size: 24px; font-weight: bold;">${labelInitial}</span>
              </div>
              <!-- Custom mood image (overlays fallback if loads successfully) -->
              <img src="${customMood.imageUrl}" alt="${moodLabel}" crossorigin="anonymous" 
                style="width: 56px; height: 56px; object-fit: contain; position: absolute; top: 0; left: 0; border-radius: 50%; background: white;"
                onerror="this.style.display='none'" />
            </div>`;
        } else if (isMainAppUnclonedMood) {
          moodDisplayHtml = `<div style="width: 56px; height: 56px; margin: 0 auto; background: linear-gradient(135deg, ${brandColor}, ${accentColor}, #D0FF0B, #0FFFE2); border-radius: 50%; display: flex; align-items: center; justify-content: center;">
            <img src="/mood-uncloned.png" alt="UnCloned" style="width: 40px; height: 40px; object-fit: contain; filter: brightness(0) invert(1);" />
          </div>`;
        } else {
          moodDisplayHtml = `<span style="font-size: 56px; line-height: 1;">${moodEmoji}</span>`;
        }
        
        // Process content - sanitize
        let processedContent = DOMPurify.sanitize(entry.content || '<p style="color: #999; font-style: italic;">No content</p>');
        processedContent = processedContent.replace(/<img /g, '<img crossorigin="anonymous" ');
        
        // Build images HTML
        let imagesHtml = '';
        if (entry.images && entry.images.length > 0) {
          imagesHtml = '<div style="margin-top: 20px; text-align: center;">';
          for (const imageId of entry.images) {
            imagesHtml += `<img src="${API_URL}/api/images/${imageId}" crossorigin="anonymous" style="max-width: 100%; max-height: 300px; width: auto; height: auto; border-radius: 12px; margin: 10px auto; display: inline-block; object-fit: cover; border: 2px solid #f0f0f0;" />`;
          }
          imagesHtml += '</div>';
        }

        // Create header container
        const headerContainer = document.createElement("div");
        headerContainer.style.cssText = `position: fixed; left: -9999px; top: 0; width: ${PAGE_WIDTH}px; background: white; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;`;
        headerContainer.innerHTML = `
          <div style="background: ${getVerticalBgStyle(brandColor, accentColor)}; padding: 25px 50px; text-align: center;">
            ${logoSection}
          </div>
          <div style="height: 4px; background: ${getHorizontalBgStyle(brandColor, accentColor)};"></div>
        `;
        document.body.appendChild(headerContainer);
        
        // Create footer container
        const footerContainer = document.createElement("div");
        footerContainer.style.cssText = `position: fixed; left: -9999px; top: 0; width: ${PAGE_WIDTH}px; background: white;`;
        footerContainer.innerHTML = `
          <div style="width: ${PAGE_WIDTH}px; padding: 15px ${MARGIN}px; background: #f8f8f8; border-top: 1px solid #e5e5e5; text-align: center; font-size: 11px; color: #888;">
            Entry ${entryIndex + 1} of ${sortedEntries.length} | Created with ❤️ using <strong>${appName}</strong>
          </div>
        `;
        document.body.appendChild(footerContainer);
        
        // Create content container (without header/footer)
        const contentContainer = document.createElement("div");
        contentContainer.style.cssText = `position: fixed; left: -9999px; top: 0; width: ${PAGE_WIDTH}px; background: white; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;`;
        contentContainer.innerHTML = `
          <div style="padding: 20px ${MARGIN}px 40px ${MARGIN}px;">
            <div style="text-align: center; margin: 0 0 12px 0;">${moodDisplayHtml}</div>
            ${entry.title ? `<h1 style="font-size: 26px; font-weight: 700; text-align: center; margin: 0 0 16px 0; color: #222;">${entry.title}</h1>` : ''}
            <div style="text-align: center; margin-bottom: 20px; display: flex; align-items: center; justify-content: center; gap: 20px; flex-wrap: wrap;">
              <img src="${dateBadgeSVG}" alt="${entry.date}" style="height: 28px; width: 120px;" />
              <span style="color: ${brandColor}; font-size: 13px; font-weight: 600;">Mood: ${moodLabel}</span>
              ${timeWritten ? `<span style="color: #777; font-size: 12px;">Written at ${timeWritten}</span>` : ''}
            </div>
            <div style="height: 1px; background: #eee; margin-bottom: 20px;"></div>
            <div style="font-size: 15px; line-height: 1.8; color: #333;">
              ${processedContent}
            </div>
            ${imagesHtml}
            ${entry.ai_reflection ? `
            <div style="margin-top: 30px; padding: 20px; background: linear-gradient(135deg, #f8f0ff, #fff0f5); border-radius: 12px; border-left: 4px solid ${brandColor};">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
                <span style="font-size: 18px;">🧠</span>
                <span style="font-size: 14px; font-weight: 700; color: ${brandColor};">AI Reflection</span>
                ${entry.ai_reflection_date ? `<span style="font-size: 10px; color: #888; margin-left: auto;">${new Date(entry.ai_reflection_date).toLocaleDateString()}</span>` : ''}
              </div>
              <div style="font-size: 12px; line-height: 1.7; color: #555; font-style: italic;">
                ${entry.ai_reflection.split('\n\n').map(p => `<p style="margin-bottom: 10px;">${p}</p>`).join('')}
              </div>
            </div>
            ` : ''}
          </div>
        `;
        document.body.appendChild(contentContainer);
        
        // Wait for images to load
        const allContainers = [headerContainer, footerContainer, contentContainer];
        for (const container of allContainers) {
          const imgs = container.querySelectorAll('img');
          await Promise.all(Array.from(imgs).map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise(resolve => { img.onload = resolve; img.onerror = resolve; });
          }));
        }
        await new Promise(resolve => setTimeout(resolve, 200));

        try {
          // Render canvases
          const headerCanvas = await html2canvas(headerContainer, {
            scale: SCALE, useCORS: true, allowTaint: false, backgroundColor: "#4A0A66", logging: false, width: PAGE_WIDTH,
          });
          
          const footerCanvas = await html2canvas(footerContainer, {
            scale: SCALE, useCORS: true, allowTaint: false, backgroundColor: "#f8f8f8", logging: false, width: PAGE_WIDTH,
          });
          
          const contentCanvas = await html2canvas(contentContainer, {
            scale: SCALE, useCORS: true, allowTaint: false, backgroundColor: "#ffffff", logging: false, width: PAGE_WIDTH,
          });

          // Calculate pages needed for this entry
          const CONTENT_TOP_PADDING = 40 * SCALE;  // Increased space between header and content
          const CONTENT_BOTTOM_PADDING = 40 * SCALE; // Increased space between content and footer
          const contentHeightPerPage = (PAGE_HEIGHT * SCALE) - headerCanvas.height - footerCanvas.height - CONTENT_TOP_PADDING - CONTENT_BOTTOM_PADDING;
          
          const scaledContentHeight = contentCanvas.height;
          
          // Helper: Find a safe break point by scanning for white/light horizontal lines
          // Also detects colored boxes (like AI reflection) and breaks BEFORE them
          const findSafeBreakPoint = (canvas, targetY, searchRange = 200 * SCALE) => {
            const ctx = canvas.getContext('2d');
            const width = canvas.width;
            const contentAreaWidth = width - (2 * MARGIN * SCALE);
            
            const isRowWhite = (y) => {
              if (y < 0 || y >= canvas.height) return false;
              const imageData = ctx.getImageData(MARGIN * SCALE, y, contentAreaWidth, 1);
              const pixels = imageData.data;
              for (let i = 0; i < pixels.length; i += 4) {
                const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
                if (r < 245 || g < 245 || b < 245) {
                  return false;
                }
              }
              return true;
            };
            
            const isRowColored = (y) => {
              if (y < 0 || y >= canvas.height) return false;
              const imageData = ctx.getImageData(MARGIN * SCALE, y, contentAreaWidth, 1);
              const pixels = imageData.data;
              let coloredPixels = 0;
              const totalPixels = pixels.length / 4;
              for (let i = 0; i < pixels.length; i += 4) {
                const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
                if (r < 250 || g < 250 || b < 250) {
                  coloredPixels++;
                }
              }
              return (coloredPixels / totalPixels) > 0.8;
            };
            
            // If inside a colored box, search for white space above it
            if (isRowColored(targetY)) {
              for (let y = targetY; y > targetY - searchRange * 2 && y > 0; y--) {
                if (isRowWhite(y)) {
                  return y;
                }
              }
            }
            
            // Standard search for white rows
            for (let y = targetY; y > targetY - searchRange && y > 0; y--) {
              if (isRowWhite(y)) {
                return y;
              }
            }
            return targetY;
          };
          
          let contentOffset = 0;
          let entryPageNum = 0;
          
          while (contentOffset < scaledContentHeight) {
            if (!isFirstPdfPage) {
              pdf.addPage();
            }
            isFirstPdfPage = false;
            
            // Create page canvas
            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = PAGE_WIDTH * SCALE;
            pageCanvas.height = PAGE_HEIGHT * SCALE;
            const ctx = pageCanvas.getContext('2d');
            
            // White background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
            
            // Draw header
            ctx.drawImage(headerCanvas, 0, 0);
            const contentStartY = headerCanvas.height + CONTENT_TOP_PADDING;
            
            // Calculate content to draw with safe break point
            const remainingContent = scaledContentHeight - contentOffset;
            let contentToDraw = Math.min(contentHeightPerPage, remainingContent);
            
            if (remainingContent > contentHeightPerPage) {
              const idealBreakY = contentOffset + contentHeightPerPage;
              const safeBreakY = findSafeBreakPoint(contentCanvas, idealBreakY);
              contentToDraw = safeBreakY - contentOffset;
              
              if (contentToDraw < 200 * SCALE) {
                contentToDraw = Math.min(contentHeightPerPage, remainingContent);
              }
            }
            
            if (contentToDraw > 0) {
              ctx.drawImage(
                contentCanvas,
                0, contentOffset,
                contentCanvas.width, contentToDraw,
                0, contentStartY,
                contentCanvas.width, contentToDraw
              );
            }
            
            // Draw footer
            ctx.drawImage(footerCanvas, 0, (PAGE_HEIGHT * SCALE) - footerCanvas.height);
            
            // Add to PDF
            const imgData = pageCanvas.toDataURL("image/jpeg", 0.90);
            pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
            
            contentOffset += contentToDraw;
            entryPageNum++;
            
            // Safety: prevent infinite loop
            if (entryPageNum > 50) break;
          }
        } catch (error) {
          console.error(`Error generating entry ${entryIndex + 1}:`, error);
        } finally {
          document.body.removeChild(headerContainer);
          document.body.removeChild(footerContainer);
          document.body.removeChild(contentContainer);
        }
      }

      const dates = entries.map(e => e.date).sort();
      const filename = `journal_entries_${dates[0]}_to_${dates[dates.length - 1]}.pdf`;
      pdf.save(filename);
      
      // Track PDF export for analytics (bulk export)
      trackPdfExport(null, "bulk", entries.length);
      
      toast.success(`PDF with ${entries.length} entries downloaded! ✨`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <Button
      onClick={handleDownloadAll}
      disabled={!entries || entries.length === 0}
      className="flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
      style={{ background: 'var(--theme-button-bg)', color: 'white' }}
      data-testid="download-all-entries-btn"
    >
      <FileText className="w-4 h-4" />
      Export All ({entries?.length || 0})
    </Button>
  );
};
